import { Report as PbiReport } from "powerbi-client"
import {DeepCopy} from "../../../api/models-ex/deep-copy";
import {UserStateEx} from "../../../api/models-ex/user-state-ex";
import {DialogResultType} from "../../../dialogs/DialogResult";
import {MessageDialogComponent} from "../../../dialogs/message-dialog/message-dialog.component";
import {ConfirmDialogComponent} from "../../../dialogs/confirm-dialog/confirm-dialog.component";
import {InteractivesService} from "../../../api/services/interactives.service";
import {US_Interactive} from "../../../api/models/us_interactive";
import {US_ModifiedInteractive} from "../../../api/models/us_modified-interactive";
import {UserState} from "../../../api/models/user-state";
import {MatDialog} from "@angular/material/dialog";
import {GlobalApp} from "../../../tools/global";
import {US_Bookmark} from "../../../api/models/us_bookmark";
import {
  EditInteractiveBookmarkDialogComponent,
  EditInteractiveBookmarkDialogData
} from "../../../dialogs/edit-interactive-bookmark-dialog/edit-interactive-bookmark-dialog.component";
import {SubscriptionService} from "../../../api/services/subscription.service";

export class BookmarkService {

  public interactBookmarks: US_Bookmark[] = [];

  constructor(
    private userId: number | null,
    private userState: UserState,
    private dialog: MatDialog,
    private app: GlobalApp,
    private loadUserState: {(): Promise<UserState>},
    private subscriptionService: SubscriptionService,
    private interactiveId: string | undefined,
    private modifiedInteractiveId: string | undefined,
    private currentInteractive: US_Interactive,
    private currentModifiedInteractive: US_ModifiedInteractive,
    private embeddedReport: PbiReport,
    private interactiveService: InteractivesService,
    public selectedBookmark: US_Bookmark = null
  ) {
  }

  getBookmarkStateById(bookmarkId: number) : string | undefined {
    if (this.currentInteractive) {
      return this.currentInteractive.bookmarks.find(x => x.bookmarkId === bookmarkId)?.reportBookmarkState;
    }
    return undefined;
  }

  async captureBookmarkState(): Promise<string> {
    return (await this.embeddedReport.bookmarksManager.capture({ personalizeVisuals: true, allPages: true})).state;
  }

  async applyBookmarkState(state: string) {
    await this.embeddedReport.bookmarksManager.applyState(state);
  }

  refreshState(): Promise<void> {
    return new Promise<void>((resolve, error) => {
      this.loadUserState().then(result => {
        this.userState = result;
        if (this.currentInteractive) {
          this.currentInteractive = this.userState.interactives.find(x => x.interactId === this.currentInteractive.interactId);
        } else if (this.currentModifiedInteractive) {
          this.currentModifiedInteractive = this.userState.modifiedInteractives.find(x => x.modInteractId === this.currentModifiedInteractive.modInteractId);
        }

        this.loadBookmarks();
        resolve();
      })
    });
  }

  applyIfBookmark(bookmarkId: string) {
    if (bookmarkId !== null && bookmarkId !== '-1') {
      for (let i = 0; i < this.interactBookmarks.length; i++) {
        if (this.interactBookmarks[i].bookmarkId == parseInt(bookmarkId)) {
          this.selectedBookmark = this.interactBookmarks[i];
          this.applyBookmark();
          break;
        }
      }
    }
  }

  loadBookmarks(): void {
    if (this.currentInteractive) {
      this.interactBookmarks = this.currentInteractive.bookmarks;
    } else if (this.currentModifiedInteractive) {
      this.interactBookmarks = this.currentModifiedInteractive.bookmarks;
    }
  }

  loadSelectedBookmark(bookmark: US_Bookmark): boolean {
    this.selectedBookmark = bookmark;
    this.applyBookmark();
    return false;
  }

  applyBookmark(): boolean {
    if (this.selectedBookmark !== null) {
      this.embeddedReport.bookmarksManager.applyState(
        JSON.parse(this.selectedBookmark.reportBookmarkState).state
      );

      this.interactiveService.interactivesLogAppliedBookmark({
        interactiveId: this.currentInteractive ? parseInt(this.interactiveId) : undefined,
        modifiedInteractiveId: this.currentModifiedInteractive ? parseInt(this.modifiedInteractiveId) : undefined,
        bookmarkId: this.selectedBookmark.bookmarkId,
      }).subscribe(_ => {})
    }

    return false;
  }

  editSelectedBookmark(bookmark: US_Bookmark): boolean {
    this.selectedBookmark = bookmark;
    this.editBookmark();
    return false;
  }

  editBookmark(): boolean {
    if (this.selectedBookmark != null) {
      if (this.selectedBookmark.canEdit) {
        let bookmark = DeepCopy.copy(this.selectedBookmark);

        const dialogData: EditInteractiveBookmarkDialogData = {
          userSubscriptions: this.currentInteractive
            ? this.currentInteractive.subscriptionHeaders
            : this.currentModifiedInteractive.subscriptionHeaders,
          bookmark,
          shareState: UserStateEx.getBookmarkShareState(
            this.userState,
            bookmark
          ),
          isNew: false,
          isOwner: this.selectedBookmark.owner,
          updateData: false,
        };

        const dialogRef = this.dialog.open(
          EditInteractiveBookmarkDialogComponent,
          {
            width: "500px",
            data: dialogData,
          }
        );

        dialogRef.afterClosed().subscribe((result) => {
          if (
            result !== null &&
            result.ResultType !== null &&
            result.ResultType === DialogResultType.Ok
          ) {
            const data = result.ResultData as EditInteractiveBookmarkDialogData;

            this.embeddedReport.bookmarksManager
              .capture({ personalizeVisuals: true, allPages: true})
              .then((bookmarkData) => {
                if (data.updateData) {
                  data.bookmark.reportBookmarkState = JSON.stringify(
                    bookmarkData
                  );
                }

                this.app.pushLoading("Saving Bookmark");

                this.interactiveService
                  .interactivesSaveInteractiveBookmark({ bookmark: data.bookmark, userId: this.userId})
                  .subscribe(
                    (result) => {
                      if (result === 0) {
                        MessageDialogComponent.showDialog(
                          this.dialog,
                          "Error",
                          "Not Authorized To Modify Bookmark"
                        );
                        this.app.popLoading();
                      } else if (result === -1) {
                        MessageDialogComponent.showDialog(
                          this.dialog,
                          "Error",
                          "Invalid User"
                        );
                        this.app.popLoading();
                      } else if (result > 0) {
                        this.interactiveService
                          .interactivesUpdateBookmarkSharing({
                            bookmarkId: result,
                            updateSharingStatus: UserStateEx.getSharingStatus(
                              data.shareState
                            ),
                          })
                          .subscribe((_) => {
                            this.refreshState().then(() => {
                              this.app.popLoading();
                            });
                          });
                      }
                    },
                    (_) => {}
                  );
              });
          }
        });
      }
    }

    return false;
  }

  deleteSelectedBookmark(bookmark: US_Bookmark): boolean {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Confirm Delete",
      "Are you sure you want to delete this bookmark?"
    ).subscribe((result) => {
      if (result.ResultType === DialogResultType.Ok) {
        this.selectedBookmark = bookmark;
        this.deleteBookmark();
      }
    });
    return false;
  }

  deleteBookmark(): boolean {
    if (this.selectedBookmark != null) {
      this.app.pushLoading("Deleting Bookmark");
      this.interactiveService
        .interactivesDeleteInteractiveBookmark({bookmarkId: this.selectedBookmark.bookmarkId, userId: this.userId})
        .subscribe(
          (result) => {
            if (result) {
              this.refreshState().then(() => {
                this.app.popLoading();
              });
            }
          },
          (_) => {}
        );
    }

    return false;
  }

  createBookmark(): boolean {
    this.embeddedReport.bookmarksManager.capture({ personalizeVisuals: true, allPages: true}).then((bookmarkData) => {
      const bookmark: US_Bookmark = {
        bookmarkId: -1,
        interactId: this.interactiveId ? parseInt(this.interactiveId) : null,
        modifiedInteractId: this.currentModifiedInteractive ? this.currentModifiedInteractive.modInteractId : null,
        subscriptionId: this.currentInteractive
          ? this.currentInteractive.subscriptionHeaders[0].subscriptionId
          : this.currentModifiedInteractive.subscriptionHeaders[0].subscriptionId,
        userId: -1,
        name: "<New Bookmark>",
        reportBookmarkState: JSON.stringify(bookmarkData),
        owner: false,
        createdBy: "",
        organizationName: "",
        shares: [],
        subscriptionName: "",
        canEdit: true,
        deleted: false,
      };

      let dialogData: EditInteractiveBookmarkDialogData = {
        userSubscriptions: this.currentInteractive ? this.currentInteractive.subscriptionHeaders : this.currentModifiedInteractive.subscriptionHeaders,
        bookmark,
        shareState: UserStateEx.getBookmarkShareState(this.userState, bookmark),
        isNew: true,
        isOwner: true,
        updateData: false,
      };

      const dialogRef = this.dialog.open(
        EditInteractiveBookmarkDialogComponent,
        {
          width: "500px",
          data: dialogData,
        }
      );

      dialogRef.afterClosed().subscribe((result) => {
        if (
          result !== null &&
          result.ResultType !== null &&
          result.ResultType === DialogResultType.Ok
        ) {
          const data = result.ResultData as EditInteractiveBookmarkDialogData;

          this.app.pushLoading("Creating Bookmark");
          this.interactiveService
            .interactivesSaveInteractiveBookmark({bookmark: data.bookmark, userId: this.userId})
            .subscribe(
              (result) => {
                //0 = Not Authorized
                //-1 = Invalid User
                // > 0 = FilterId

                if (result === 0) {
                  MessageDialogComponent.showDialog(
                    this.dialog,
                    "Error",
                    "Not Authorized To Modify Bookmark"
                  );
                  this.app.popLoading();
                } else if (result === -1) {
                  MessageDialogComponent.showDialog(
                    this.dialog,
                    "Error",
                    "Invalid User"
                  );
                  this.app.popLoading();
                } else if (result > 0) {
                  this.interactiveService
                    .interactivesUpdateBookmarkSharing({
                      bookmarkId: result,
                      updateSharingStatus: UserStateEx.getSharingStatus(
                        data.shareState
                      ),
                    })
                    .subscribe((_) => {
                      this.refreshState().then(() => {
                        this.app.popLoading();
                      });
                    });
                }
              },
              (_) => {}
            );
        }
      });
    });

    return false;
  }
}
